import { effect, inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { HighchartsCommonService } from '../highcharts-common/highcharts-common.service';
import { Theme } from '../../model/user/theme.enum';
import { CoreConstant } from '../../constant/base.constant';

@Injectable({
    providedIn: 'root',
})
export class ThemeService implements OnDestroy {
    private readonly _highchartsCommonService: HighchartsCommonService = inject(HighchartsCommonService);
    private readonly _userTheme: WritableSignal<Theme> = signal(Theme.light);
    private readonly _localStorage: Storage = window.localStorage;

    constructor() {
        this._userTheme.set((localStorage.getItem(CoreConstant.VC_THEME) as Theme) ?? Theme.light);
    }

    syncStorage = effect(() => {
        localStorage.setItem(CoreConstant.VC_THEME, `${this._userTheme()}`);
    });

    setTheme(theme: Theme = Theme.light): void {
        const html = document.querySelector('html');
        if (!html) return;

        switch (theme) {
            case Theme.dark:
                document.body.classList.remove('vc-light-theme');
                html.dataset.theme = `theme-dark`;
                document.body.classList.add('vc-dark-theme');
                break;
            case Theme.light:
            default:
                document.body.classList.remove('vc-dark-theme');
                html.dataset.theme = `theme-light`;
                document.body.classList.add('vc-light-theme');
        }

        this._userTheme.set(theme);
        this._highchartsCommonService.initializeHighCharts();
        this._highchartsCommonService.redrawChartColors();
    }

    getThemeAndSet(): void {
        const theme = this._localStorage.getItem(CoreConstant.VC_THEME) as Theme;
        this.setTheme(theme);
    }

    getTheme(): Theme {
        return this._userTheme();
    }

    ngOnDestroy() {
        this._userTheme.set(Theme.light);
    }
}
